import React, { useState, useEffect } from 'react';

import {
  Box,
  Card,
  Stack,
  Button,
  Avatar,
  Collapse,
  CardMedia,
  CardHeader,
  IconButton,
  Typography,
  CardActions,
  CircularProgress,
} from '@mui/material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';

import { useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { isEmptyObject } from 'helpers/formatObject';
import { digitsEnToFa } from '@persian-tools/persian-tools';

export default function NewAssignmentPopup({ title, onClose, selected, onAssign, assigning, handleSelect, graph }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState([]);
  const { userToken } = useSelector((state) => state.auth);

  const onOpen = () => setOpen(!open);

  const [getData, { loading }] = useLazyQuery(graph.assignment.query, {
    context: {
      serviceName: graph.assignment.serviceName,
      headers: {
        authorization: `Bearer ${userToken}`,
      },
    },
  });

  const handleData = async () => {
    try {
      if (open) {
        const { data } = await getData({
          variables: {
            ids: selected.map((item) => item.id),
          },
        });
        if (!isEmptyObject(data)) {
          const res = data[graph.assignment.name];
          setResult(selected.length > 0 ? res?.data : []);
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    handleData();
  }, [selected, open]);

  return (
    <Box rowGap={1} bgcolor="action.hover">
      <Card
        sx={{
          height: '100%',
          borderRadius: 0,
          bgcolor: 'transparent',
        }}
      >
        <CardHeader
          sx={{ p: 1 }}
          title="مورد انتخاب شده"
          avatar={
            <Avatar sx={{ bgcolor: 'primary.main', color: '#fff' }}>
              <Typography variant="h6">{digitsEnToFa(selected.length)}</Typography>
            </Avatar>
          }
          action={
            <IconButton
              color="primary"
              onClick={onOpen}
              disabled={selected.length === 0}
              sx={{ bgcolor: 'action.hover', width: 32, height: 32 }}
            >
              {loading ? (
                <CircularProgress color="primary" size={18} />
              ) : open ? (
                <FullscreenExitRoundedIcon fontSize="small" />
              ) : (
                <FullscreenRoundedIcon fontSize="small" />
              )}
            </IconButton>
          }
        />
        <Collapse in={open}>
          <Stack direction="row" columnGap={0.5}>
            <Box
              sx={{
                px: 1,
                columnGap: 2,
                width: '100%',
                display: 'flex',
                borderRadius: 2,
                scrollPadding: 24,
                overflowY: 'hidden',
                scrollbarWidth: 'none',
                scrollBehavior: 'smooth',
                scrollSnapType: 'x mandatory',
                overflowX: 'scroll !important',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {result.map((item, i) => {
                const { id, title, media } = item;
                const img = media ? media[0]?.full_url : '/assets/images/picture.png';
                return (
                  <Box key={i} sx={{ scrollSnapAlign: 'center' }}>
                    <Card
                      variant="outlined"
                      sx={{
                        width: 180,
                        flexDirection: 'row',
                      }}
                    >
                      <CardMedia alt={title} image={img} sx={{ height: 56 }} />
                      <CardHeader
                        sx={{ p: 1 }}
                        title={
                          <Typography fontSize={14} variant="subtitle1">
                            {title}
                          </Typography>
                        }
                        action={
                          <IconButton
                            size="small"
                            sx={{ bgcolor: 'action.hover' }}
                            onClick={() => handleSelect({ id, title })}
                          >
                            <DeleteRoundedIcon fontSize="small" color="error" />
                          </IconButton>
                        }
                      />
                    </Card>
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </Collapse>
        <CardActions sx={{ py: 1, justifyContent: 'flex-end', height: 60 }}>
          <Button size="medium" color="inherit" onClick={onClose} sx={{ width: 96, bgcolor: 'action.hover' }}>
            لغو
          </Button>
          <Button
            size="medium"
            color="primary"
            variant="contained"
            disabled={assigning}
            sx={{ width: 96 }}
            onClick={() => onAssign(selected, onClose)}
          >
            اختصاص
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
}
